import * as React from 'react'
import { clsx } from 'clsx'

import {
  Block,
  normalizeStringPath,
  type ReferenceBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

import * as PropertiesPanel from '../../properties-panel'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getAllPossibleTypeOptions } from '@basehub/data/replicache/schema/block/reference/util'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { BlockIcon } from '~/components/block-icon'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import {
  CreateBlockParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { getId } from '@basehub/utils/id/index'
import { useRepoStore } from '../../repo-store'

export const ReferenceBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: ReferenceBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const undoManager = useRepoUndoManager()
  const { encodedSegment } = useBranchOrCommitSegment()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const componentsSet = useWorkingTreeStore(
    (s) =>
      (s.tree?.compoundIndexes.get('components') ?? new Set()) as Set<string>
  )
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)

  const typeOptions = React.useMemo(() => {
    if (!getNode || !componentsSet || !getChildrenKeys) return []
    return getAllPossibleTypeOptions(
      { getNode, componentsSet, getChildrenKeys },
      block
    )
  }, [getNode, componentsSet, getChildrenKeys, block])

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Is required"
          value={block.constraints.isRequired}
          id={`el-${sourceBlockId}-pp-is-required`}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { isRequired: v },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Can link existing"
          id={`el-${sourceBlockId}-pp-can-link-existing`}
          value={!block.constraints.disableLinkExisting}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: { constraints: { disableLinkExisting: !v } },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-can-link-existing`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-can-link-existing`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />

        <PropertiesPanel.BooleanOption
          name="Can create new"
          id={`el-${sourceBlockId}-pp-can-create-new`}
          value={!block.constraints.disableCreateNew}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: { constraints: { disableCreateNew: !v } },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-can-create-new`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-can-create-new`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Allow multiple"
          id={`${sourceBlockId}-pp-allow-multiple`}
          value={block.constraints.multiple}
          mutate={async (v) => {
            const params: UpdateBlockParams & {
              data: UpdateBlockParams['data'] & {
                constraints: Partial<ReferenceBlock['constraints']>
              }
            } = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { multiple: v },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            if (!v) {
              params.data.constraints.min = null
              params.data.constraints.max = null
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`${sourceBlockId}-pp-allow-multiple`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`${sourceBlockId}-pp-allow-multiple`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        {block.constraints.multiple && (
          <>
            <PropertiesPanel.LimitInput
              name="Limit"
              maxId={`${sourceBlockId}-pp-limit-max`}
              minId={`${sourceBlockId}-pp-limit-min`}
              value={{
                min: block.constraints.min ?? null,
                max: block.constraints.max ?? null,
              }}
              mutateMin={async (v) => {
                const params: UpdateBlockParams = {
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: { constraints: { min: v } },
                  path,
                  strategy: 'merge-deep',
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(`${sourceBlockId}-pp-limit-min`),
                      { op: 'updateBlock', args: params },
                    ],
                    undo: [
                      getInputFocusOp(`${sourceBlockId}-pp-limit-min`),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
              }}
              mutateMax={async (v) => {
                let params: UpdateBlockParams
                if (!v) {
                  params = {
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: { constraints: { max: null } },
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                } else {
                  const num = Number(v)
                  params = {
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: { constraints: { max: num } },
                    path,
                    strategy: 'merge-deep',
                    isoNow: new Date().toISOString(),
                  }
                }
                const result = await replicache?.mutate.updateBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(`${sourceBlockId}-pp-max`),
                      { op: 'updateBlock', args: params },
                    ],
                    undo: [
                      getInputFocusOp(`${sourceBlockId}-pp-max`),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
              }}
            />
          </>
        )}
      </PropertiesPanel.Group>
      {typeOptions && (
        <PropertiesPanel.Group>
          <PropertiesPanel.CheckList
            title="Reference types"
            labelClassName="!text-dark-dim !text-sm !font-medium leading-normal flex items-center w-full"
            itemName="Components"
            id={`el-${sourceBlockId}-pp-reference-types`}
            options={typeOptions.map((o) => {
              return {
                name: o.label,
                id: o.id,
                href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${o.id}`,
                icon: (
                  <BlockIcon
                    block={{ type: o.type }}
                    iconProps={{
                      width: 14,
                      height: 14,
                      className: clsx(
                        block.value.targetBlockIds.includes(o.id)
                          ? 'text-dark-dim'
                          : 'hover:text-dark-dim'
                      ),
                    }}
                  />
                ),
              }
            })}
            checkedOptions={typeOptions.reduce<
              Array<PropertiesPanel.CheckItem>
            >((acum, current) => {
              if (block.constraints.acceptedTypes?.includes(current.id)) {
                acum.push({
                  name: current.label,
                  id: current.id,
                  href: `/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${current.id}`,
                  icon: <BlockIcon block={{ type: current.type }} />,
                })
              }
              return acum
            }, [])}
            mutate={async (checked, options) => {
              const currentAcceptedTypes = [
                ...(block.constraints.acceptedTypes ?? []),
              ]
              let newAcceptedTypes: string[] | null
              if (checked === 'overwrite') {
                newAcceptedTypes = options.map((o) => o.id)
              } else {
                newAcceptedTypes = checked
                  ? currentAcceptedTypes.concat(options.map((o) => o.id))
                  : currentAcceptedTypes.filter(
                      (t) => !options.some((o) => o.id === t)
                    )
              }

              if (newAcceptedTypes.length === 0) {
                newAcceptedTypes = null
              }

              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    acceptedTypes: newAcceptedTypes as string[] as any,
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-reference-types`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-reference-types`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            addNew={{
              label: 'Add new',
              onClick: async () => {
                // will create an "internal" component
                // "internal" means its below a reference and it cannot be referenced by other references
                const newComponentId = getId()
                const params: CreateBlockParams = {
                  idSeed: getId(),
                  position: { type: 'last' },
                  data: {
                    id: newComponentId,
                    type: 'component',
                    hidden: true,
                  },
                  path: normalizeStringPath({
                    path,
                    edgeId: block.id,
                    format: 'string',
                  }),
                  parentInstanceChildTarget: block.instanceChildTarget,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.createBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(`el-${sourceBlockId}-pp-reference-types`),
                      { op: 'createBlock', args: params },
                    ],
                    undo: [
                      getInputFocusOp(`el-${sourceBlockId}-pp-reference-types`),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
                addTab({ key: newComponentId, partiallyOpen: true })
                const activeDocument = activeTabKey
                  ? getNode?.(activeTabKey, {
                      fallbackKey: 'sourceBlockId',
                    })
                  : null
                setFocus({
                  type: 'block:document-title',
                  blockId: newComponentId,
                  temporaryBackLink: {
                    title:
                      activeDocument?.value?.title ??
                      (block.title || 'Untitled'),
                    id: idToSourceBlockId(activeDocument?.value ?? block),
                    edgeId:
                      activeDocument && activeDocument.value.id !== block.id
                        ? idToSourceBlockId(block)
                        : undefined,
                  },
                })
              },
            }}
          />
        </PropertiesPanel.Group>
      )}
      <PropertiesPanel.CommonSearchConfigGroup
        block={block}
        parent={parent}
        path={path}
      />
    </PropertiesPanel.PropertiesContainer>
  )
}
